/**
 * views/reporting/sections/StatusCapacity.jsx - status donut, open bid aging
 * and win rate by account.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, CartesianGrid, XAxis, YAxis, Bar } from 'recharts';
import { Icon } from '../../../components/ui/Icon';
import { exportCsv } from '../../../utils/exportCsv';
import { exportChartPNG } from '../../../utils/exportChartPNG';
import { CSV_BTN, winRateColor } from '../reportingHelpers';
import { CARD, TT, TICK } from '../reportingTokens';
import { SectionHeader } from '../SectionHeader';

export function renderStatusCapacity({
  statusTotal,
  yearLabel,
  statusDonut,
  donutRef,
  openBidAging,
  capacityRef,
  agingBuckets,
  winRateByAccount,
  wrAcctRef,
}) {
  return (
    <>
      {/* STATUS DONUT + CAPACITY + WIN RATE LIST */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div>
          <SectionHeader
            title="Bid Status Breakdown"
            subtitle={`${statusTotal} total bids · ${yearLabel}`}
            explain={{
              title: 'Bid Status Breakdown',
              body: 'COUNT of bids per status for the selected year. Hover any slice for its % of total. "Open" is the live pipeline; "Won" + "Lost" are the closed deals.',
              formula: 'COUNT(*) grouped by status, filtered by selected year',
            }}
            right={
              <>
                <button
                  onClick={() =>
                    exportCsv(
                      'status_breakdown.csv',
                      statusDonut.map((d) => ({ Status: d.name, Count: d.value }))
                    )
                  }
                  style={CSV_BTN}
                >
                  <Icon name="download" size={10} color="#666" /> CSV
                </button>
                <button onClick={() => exportChartPNG(donutRef, 'status_breakdown')} style={CSV_BTN}>
                  <Icon name="download" size={10} color="#666" /> PNG
                </button>
              </>
            }
          />
          <div ref={donutRef} style={{ ...CARD, height: 280 }}>
            <div style={{ height: 240, position: 'relative', display: 'grid', gridTemplateColumns: '1fr 0.9fr' }}>
              {/* Donut column */}
              <div style={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDonut}
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      dataKey="value"
                      paddingAngle={2}
                      strokeWidth={2}
                      stroke="#fff"
                    >
                      {statusDonut.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={TT}
                      formatter={(v, n) => [`${v} (${statusTotal > 0 ? ((v / statusTotal) * 100).toFixed(1) : 0}%)`, n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#0D0D0D', lineHeight: 1 }}>{statusTotal}</div>
                  <div
                    style={{
                      fontSize: 8.5,
                      color: '#5A5A5A',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1.2,
                      marginTop: 2,
                    }}
                  >
                    Total Bids
                  </div>
                </div>
              </div>
              {/* Custom legend column */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6, padding: '0 6px 0 4px' }}>
                {statusDonut.map((d) => {
                  const pct = statusTotal > 0 ? ((d.value / statusTotal) * 100).toFixed(0) : 0;
                  return (
                    <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Montserrat' }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: '#444', flex: 1 }}>{d.name}</span>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: '#0D0D0D', tabularNums: true }}>{d.value}</span>
                      <span style={{ fontSize: 9, color: '#5A5A5A', fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div>
          <SectionHeader
            title="Open Bid Aging"
            subtitle={`How long each open bid has been sitting · ${yearLabel}`}
            explain={{
              title: 'Open Bid Aging',
              body: 'Days since the quote was issued for every Open bid in the selected year, bucketed by age. The >90 day bucket needs PM attention: stale quotes usually need re-pricing or follow-up.',
              formula: 'days_since_quote = today - quote_date · grouped: <30 / 30–60 / 60–90 / >90',
            }}
            right={
              <button
                onClick={() =>
                  exportCsv(
                    'open_bid_aging.csv',
                    openBidAging.map((d) => ({ Project: d.name, Customer: d.customer, Days: d.days, Value: d.value }))
                  )
                }
                style={CSV_BTN}
              >
                <Icon name="download" size={10} color="#666" /> CSV
              </button>
            }
          />
          <div ref={capacityRef} style={{ ...CARD, height: 280 }}>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingBuckets} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" vertical={false} />
                  <XAxis dataKey="name" tick={{ ...TICK, fontSize: 11, fontWeight: 700, fill: '#333' }} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip contentStyle={TT} formatter={(v) => [`${v} bids`, '']} cursor={{ fill: '#F4F4F4' }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {agingBuckets.map((b, i) => {
                      const colors = ['#1B5E20', '#1565C0', '#E65100', '#B71C1C'];
                      return <Cell key={i} fill={colors[i]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div>
          <SectionHeader
            title="Win Rate by Account"
            subtitle={`Green >70%, orange 40-70%, red <40% · ${yearLabel}`}
            right={
              <>
                <button
                  onClick={() =>
                    exportCsv(
                      'win_rate_by_account.csv',
                      winRateByAccount.map((d) => ({ Account: d.name, 'Win Rate': d.winRate, Won: d.won, Lost: d.lost }))
                    )
                  }
                  style={CSV_BTN}
                >
                  <Icon name="download" size={10} color="#666" /> CSV
                </button>
                <button onClick={() => exportChartPNG(wrAcctRef, 'win_rate_by_account')} style={CSV_BTN}>
                  <Icon name="download" size={10} color="#666" /> PNG
                </button>
              </>
            }
          />
          <div ref={wrAcctRef} style={{ ...CARD, height: 280 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, height: 240, overflowY: 'auto' }}>
              {winRateByAccount.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: '#555',
                      minWidth: 80,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {a.name}
                  </span>
                  <div style={{ flex: 1, height: 6, background: '#F0F0F0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${a.winRate}%`, height: '100%', background: winRateColor(a.winRate), borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 8, fontWeight: 700, color: winRateColor(a.winRate), minWidth: 60, textAlign: 'right' }}>
                    {a.winRate}% ({a.won}W/{a.lost}L)
                  </span>
                </div>
              ))}
              {winRateByAccount.length === 0 && (
                <div style={{ color: '#8A8A8A', fontSize: 11, textAlign: 'center', padding: 40 }}>No data</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
